/**
 * Tier-1 helper: exchange an Office SSO bootstrap token for a Product
 * API JWT.
 *
 * Office.auth.getAccessToken returns a Microsoft-signed token whose
 * `aud` claim is *not* the Product API audience (it's whatever Office
 * gave us, typically `Microsoft.Office.Outlook` or the manifest-declared
 * AAD app's client id). Product API has a small endpoint
 * `POST /api/auth/exchange-sso-token` (Agent A2) that:
 *   1. validates the SSO token (signature + tenant + Office audience),
 *   2. extracts the user identity claims, and
 *   3. issues a fresh Product API JWT scoped to the user.
 *
 * This is the canonical Microsoft-recommended path. It is dormant
 * today because the HSIP tenant returns error 13000 from
 * Office.auth.getAccessToken — the moment Microsoft fixes that, this
 * code path activates with no deploy required.
 *
 * ⚠️  SERVER-SIDE STUB IN PLACE — before this can be used in production:
 *   1. Replace `ExchangeSsoTokenHandler` in Product.Application with real
 *      JWKS validation (see TODO in ExchangeSsoTokenCommand.cs).
 *   2. Remove the env guard in `AuthController.ExchangeSsoToken` that
 *      returns 501 in non-Development environments.
 *   3. Remove the 501 fall-through guard in `useAuth.ts → tier1OfficeSso`.
 * Until then the server returns 501 in staging/prod, and the cascade
 * falls through to Tier 2 automatically.
 */

import { logger, newCorrelationId, type AuthTier } from "../obs/logger";
import { getApiBaseUrl } from "../config/apiBaseUrl";

const EXCHANGE_PATH = "/api/auth/exchange-sso-token";
const EXCHANGE_TIMEOUT_MS = 15_000;

interface ExchangeResponse {
  access_token: string;
  expires_in?: number;
  token_type?: string;
}

export class SsoExchangeError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly correlationId: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = "SsoExchangeError";
  }
}

/**
 * POST the bootstrap Office SSO token to the Product API exchange
 * endpoint. Returns the Product-API-audience JWT on success.
 *
 * Always tags the call with a fresh `x-correlation-id` so the resulting
 * Product API log row can be cross-referenced to the add-in client log
 * via the same id. The auth_tier sticks to "office_sso" because Tier 1
 * is the originator of the exchange.
 */
export async function exchangeForProductApiToken(
  ssoToken: string,
  options?: { signal?: AbortSignal },
): Promise<string> {
  const correlationId = newCorrelationId();
  const auth_tier: AuthTier = "office_sso";

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), EXCHANGE_TIMEOUT_MS);
  const externalSignal = options?.signal;
  const onAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener("abort", onAbort);
  }

  const url = `${getApiBaseUrl()}${EXCHANGE_PATH}`;
  logger.info("Tier 1 SSO exchange: posting bootstrap token", {
    correlation_id: correlationId,
    auth_tier,
    phase: "sso_exchange",
  });

  try {
    const resp = await fetch(url, {
      method: "POST",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        "x-correlation-id": correlationId,
      },
      body: JSON.stringify({ office_sso_token: ssoToken }),
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new SsoExchangeError(
        `SSO exchange failed: HTTP ${resp.status}: ${text}`,
        resp.status,
        correlationId,
      );
    }
    const data = (await resp.json()) as ExchangeResponse;
    if (!data.access_token) {
      throw new SsoExchangeError(
        "SSO exchange returned empty access_token",
        resp.status,
        correlationId,
      );
    }
    logger.info("Tier 1 SSO exchange: success", {
      correlation_id: correlationId,
      auth_tier,
      phase: "sso_exchange",
    });
    return data.access_token;
  } catch (err) {
    if (err instanceof SsoExchangeError) throw err;
    const status = controller.signal.aborted ? null : null;
    logger.warn("Tier 1 SSO exchange: failed", {
      correlation_id: correlationId,
      auth_tier,
      phase: "sso_exchange",
      error: String(err),
    });
    throw new SsoExchangeError(
      `SSO exchange failed: ${String(err)}`,
      status,
      correlationId,
      err,
    );
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) externalSignal.removeEventListener("abort", onAbort);
  }
}
